import { strFromU8, unzipSync } from 'fflate';
import type { ImpuestoDeGasto } from '@tanteo/compartido';

/**
 * Lee el documento electrónico del proveedor desde su XML, el original fiscal; también acepta el
 * `.zip` del correo. Los nodos se buscan por nombre local y por ruta, no por prefijo: `CompanyID`
 * aparece bajo el emisor y bajo el adquiriente, y sin la ruta se confunden.
 */

interface DocumentoProveedor {
  cufe: string | null;
  nitEmisor: string | null;
  razonSocialEmisor: string | null;
  /** AAAA-MM-DD, tal como lo trae el documento. */
  fecha: string | null;
  subtotal: number | null;
  /** Un tributo por cada `TaxTotal` del documento, con el código y el nombre que él declara. */
  impuestos: ImpuestoDeGasto[];
  total: number | null;
}

type LecturaDocumento =
  | { estado: 'leido'; documento: DocumentoProveedor }
  | { estado: 'rechazado'; motivo: string };

const TAMANO_MAX = 15 * 1024 * 1024;

/** Los hijos directos con ese nombre local, sin importar el prefijo del espacio de nombres. */
function hijos(nodo: Element, nombre: string): Element[] {
  return [...nodo.children].filter((h) => h.localName === nombre);
}

/** Baja por la ruta de nombres locales y devuelve el primer nodo que la cumple. */
function porRuta(raiz: Element, ruta: string[]): Element | null {
  let actual: Element | null = raiz;
  for (const paso of ruta) {
    if (!actual) return null;
    actual = hijos(actual, paso)[0] ?? null;
  }
  return actual;
}

function texto(raiz: Element, ruta: string[]): string | null {
  const nodo = porRuta(raiz, ruta);
  const valor = nodo?.textContent?.trim();
  return valor ? valor : null;
}

function numero(raiz: Element, ruta: string[]): number | null {
  const valor = texto(raiz, ruta);
  if (valor === null) return null;
  const n = Number(valor.replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? Math.round(n) : null;
}

/** La tarifa se lee sin redondear: el 1,5% de algunos tributos no es 2%. */
function decimal(raiz: Element, ruta: string[]): number | null {
  const valor = texto(raiz, ruta);
  if (valor === null) return null;
  const n = Number(valor.replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

/**
 * Los tributos del documento, uno por `TaxTotal`. Solo el esquema `01` del catálogo DIAN es IVA;
 * el resto queda como `otro` con su nombre a la vista, para que la persona lo tipifique.
 */
function impuestosDe(documento: Element): ImpuestoDeGasto[] {
  const impuestos: ImpuestoDeGasto[] = [];
  for (const total of hijos(documento, 'TaxTotal')) {
    const monto = numero(total, ['TaxAmount']);
    if (monto === null) continue;
    const subtotal = hijos(total, 'TaxSubtotal')[0] ?? null;
    const esquema = subtotal ? porRuta(subtotal, ['TaxCategory', 'TaxScheme']) : null;
    const codigo = esquema ? texto(esquema, ['ID']) : null;
    const nombre = esquema ? texto(esquema, ['Name']) : null;
    const tarifa = subtotal ? decimal(subtotal, ['TaxCategory', 'Percent']) : null;
    impuestos.push({
      tipo: codigo === '01' ? 'iva' : 'otro',
      nombre: codigo === '01' ? nombre : (nombre ?? `Tributo ${codigo ?? 'sin código'}`),
      codigoDian: codigo,
      tarifa,
      monto,
    });
  }
  return impuestos;
}

/** El nodo de la factura: puede venir suelto o dentro de un AttachedDocument, como texto escapado. */
function nodoDelDocumento(xml: string): Element | null {
  const doc = new DOMParser().parseFromString(xml, 'text/xml');
  const raiz = doc.documentElement;
  if (!raiz || raiz.localName === 'parsererror') return null;
  if (raiz.localName === 'Invoice') return raiz;
  const dentro = texto(raiz, ['Attachment', 'ExternalReference', 'Description']);
  if (!dentro) return null;
  const interno = new DOMParser().parseFromString(dentro, 'text/xml').documentElement;
  return interno && interno.localName === 'Invoice' ? interno : null;
}

export function interpretarDocumentoXml(xml: string): DocumentoProveedor | null {
  const doc = nodoDelDocumento(xml);
  if (!doc) return null;
  const emisor = porRuta(doc, ['AccountingSupplierParty', 'Party']);
  const razon = emisor
    ? texto(emisor, ['PartyTaxScheme', 'RegistrationName']) ??
      texto(emisor, ['PartyLegalEntity', 'RegistrationName']) ??
      texto(emisor, ['PartyName', 'Name'])
    : null;
  return {
    cufe: texto(doc, ['UUID']),
    nitEmisor: emisor ? texto(emisor, ['PartyTaxScheme', 'CompanyID']) : null,
    razonSocialEmisor: razon,
    fecha: texto(doc, ['IssueDate']),
    subtotal:
      numero(doc, ['LegalMonetaryTotal', 'LineExtensionAmount']) ??
      numero(doc, ['LegalMonetaryTotal', 'TaxExclusiveAmount']),
    impuestos: impuestosDe(doc),
    total: numero(doc, ['LegalMonetaryTotal', 'PayableAmount']),
  };
}

/** El XML de dentro de un zip: el que más pesa, que es el documento y no la firma ni el manifiesto. */
function xmlDelZip(datos: Uint8Array): string | null {
  const entradas = unzipSync(datos);
  const candidatos = Object.entries(entradas)
    .filter(([nombre]) => /\.xml$/i.test(nombre))
    .sort((a, b) => b[1].length - a[1].length);
  const elegido = candidatos[0];
  return elegido ? strFromU8(elegido[1]) : null;
}

export async function leerDocumentoDeArchivo(archivo: File): Promise<LecturaDocumento> {
  if (archivo.size > TAMANO_MAX) return { estado: 'rechazado', motivo: 'El archivo supera los 15 MB.' };
  const esZip = /\.zip$/i.test(archivo.name);
  if (!esZip && !/\.xml$/i.test(archivo.name)) {
    return { estado: 'rechazado', motivo: 'Sube el XML del documento, o el .zip que te llegó con él.' };
  }
  try {
    const xml = esZip ? xmlDelZip(new Uint8Array(await archivo.arrayBuffer())) : await archivo.text();
    if (!xml) return { estado: 'rechazado', motivo: 'Ese .zip no trae ningún XML dentro.' };
    const documento = interpretarDocumentoXml(xml);
    if (!documento) {
      return {
        estado: 'rechazado',
        motivo: 'Ese XML no es un documento electrónico que podamos leer. Sube el que te envió el proveedor.',
      };
    }
    return { estado: 'leido', documento };
  } catch {
    return { estado: 'rechazado', motivo: 'No pudimos abrir el archivo. ¿Está completo?' };
  }
}
